import { z } from "zod";

/** Inpatient Phase 6 — Emergency Department: arrival, triage, tracking board. */

export const edStatusSchema = z.enum([
	"waiting",
	"in_treatment",
	"admitted",
	"discharged",
	"transferred",
	"lwbs",
]);
export type EdStatus = z.infer<typeof edStatusSchema>;

export const edVisitApiSchema = z.object({
	id: z.string().uuid(),
	clinic_id: z.string().uuid(),
	patient_id: z.string().uuid(),
	chief_complaint: z.string(),
	triage_level: z.number().int().nullable(),
	arrival_at: z.string().datetime(),
	status: z.string(),
	bp_systolic: z.number().int().nullable(),
	bp_diastolic: z.number().int().nullable(),
	pulse: z.number().int().nullable(),
	temp_c: z.number().nullable(),
	spo2: z.number().int().nullable(),
	resp_rate: z.number().int().nullable(),
	attending_member_id: z.string().uuid().nullable(),
	disposition: z.string().nullable(),
	disposition_at: z.string().datetime().nullable(),
	admission_id: z.string().uuid().nullable(),
	notes: z.string().nullable(),
	created_at: z.string().datetime(),
	patient_name: z.string().optional(),
});
export type EdVisitApi = z.infer<typeof edVisitApiSchema>;

const intIn = (min: number, max: number) =>
	z.coerce.number().int().min(min).max(max).nullable().optional();

export const registerEdVisitInputSchema = z.object({
	patient_id: z.string().uuid(),
	chief_complaint: z.string().min(1).max(1000),
	triage_level: z.coerce.number().int().min(1).max(5).nullable().optional(),
	bp_systolic: intIn(40, 300),
	bp_diastolic: intIn(20, 200),
	pulse: intIn(20, 300),
	temp_c: z.coerce.number().min(25).max(45).nullable().optional(),
	spo2: intIn(40, 100),
	resp_rate: intIn(4, 80),
	notes: z.string().max(2000).nullable().optional(),
});
export type RegisterEdVisitInput = z.infer<typeof registerEdVisitInputSchema>;

export const updateEdTriageInputSchema = z.object({
	triage_level: z.coerce.number().int().min(1).max(5).nullable().optional(),
	bp_systolic: intIn(40, 300),
	bp_diastolic: intIn(20, 200),
	pulse: intIn(20, 300),
	temp_c: z.coerce.number().min(25).max(45).nullable().optional(),
	spo2: intIn(40, 100),
	resp_rate: intIn(4, 80),
});
export type UpdateEdTriageInput = z.infer<typeof updateEdTriageInputSchema>;

export const updateEdStatusInputSchema = z.object({
	status: z.enum([
		"in_treatment",
		"admitted",
		"discharged",
		"transferred",
		"lwbs",
	]),
	disposition: z.string().max(1000).nullable().optional(),
});
export type UpdateEdStatusInput = z.infer<typeof updateEdStatusInputSchema>;
